import React from 'react';
import { Flex, Segmented, Tooltip } from 'antd';
import AddButton from '@/components/AddButton';
import { DownloadIcon } from '@/assets/icons/icons';
import ShipmentsFilterPanel from '@/pages/Shipments/ControlTower/components/ShipmentFiltersPanel';
import ShipmentSearchControl from '@/pages/Shipments/ControlTower/components/ShipmentSearchControl';
import ShipmentSort from '@/pages/Shipments/ControlTower/components/ShipmentSort';
import type {
  AppliedFilter,
  FilterArrayItem,
  ShipmentFiltersPayload,
} from '@/pages/Shipments/ControlTower/types';
import {
  inventoryFiltersConfig,
  PER_DEVICE_HIDDEN_FILTER_KEYS,
} from '../utils/inventoryFiltersConfig';
import type {
  InventoryGroupField,
  InventorySortDirection,
  InventorySortKey,
  InventoryViewMode,
} from '../type';
import InventoryGroupingControl from './InventoryGroupingControl';
import type { InventorySortOption } from './InventorySortControl';

type InventoryToolbarProps = {
  totalRangeStart: number;
  totalRangeEnd: number;
  totalCount: number;
  isSearchVisible: boolean;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setIsSearchVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  handleSearchSubmit: (value?: string) => void;
  handleSearchIconClick: () => void;
  handleCloseSearch: () => void;
  groupBy: InventoryGroupField;
  subGroupBy: InventoryGroupField;
  setGroupBy: React.Dispatch<React.SetStateAction<InventoryGroupField>>;
  setSubGroupBy: React.Dispatch<React.SetStateAction<InventoryGroupField>>;
  dropdownMaxHeight: string;
  appliedFilters: AppliedFilter[];
  setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFilter[]>>;
  filterArray: FilterArrayItem[];
  setFilterArray: React.Dispatch<React.SetStateAction<FilterArrayItem[]>>;
  setFiltersPayload: React.Dispatch<
    React.SetStateAction<ShipmentFiltersPayload | null>
  >;
  page: number;
  pageSize: number;
  inventoryView: InventoryViewMode;
  sortOptions: InventorySortOption[];
  sortKey: InventorySortKey;
  sortDirection: InventorySortDirection;
  onRunSearch: (args: {
    query: string;
    check: boolean;
    page?: number;
    pageSize?: number;
    calledFrom?: string;
    payload?: ShipmentFiltersPayload | null;
  }) => void;
  onAddInventory: () => void;
  onSortChange: (
    key: InventorySortKey,
    direction: InventorySortDirection
  ) => void;
  onInventoryViewChange: (view: InventoryViewMode) => void;
  onDownload: () => void;
  isDownloading?: boolean;
};

const InventoryToolbar: React.FC<InventoryToolbarProps> = ({
  totalRangeStart,
  totalRangeEnd,
  totalCount,
  isSearchVisible,
  searchValue,
  setSearchValue,
  setIsSearchVisible,
  setPage,
  handleSearchSubmit,
  handleSearchIconClick,
  handleCloseSearch,
  groupBy,
  subGroupBy,
  setGroupBy,
  setSubGroupBy,
  dropdownMaxHeight,
  appliedFilters,
  setAppliedFilters,
  filterArray,
  setFilterArray,
  setFiltersPayload,
  page,
  pageSize,
  inventoryView,
  sortOptions,
  sortKey,
  sortDirection,
  onRunSearch,
  onAddInventory,
  onSortChange,
  onInventoryViewChange,
  onDownload,
  isDownloading = false,
}) => {
  const isPerDevice = inventoryView === 'per_device';
  const [searchPopoverOpen, setSearchPopoverOpen] = React.useState(false);
  const [windowWidth, setWindowWidth] = React.useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCompactSearch = windowWidth > 766 && windowWidth < 1400;

  const handleInventorySearchIconClick = () => {
    if (isCompactSearch) {
      setSearchPopoverOpen(true);
      return;
    }

    handleSearchIconClick();
  };

  const handleInventoryCloseSearch = () => {
    setSearchPopoverOpen(false);
    handleCloseSearch();
  };

  const handleInventorySearchSubmit = () => {
    handleSearchSubmit();
    setSearchPopoverOpen(false);
  };

  const handleInventorySearchQueryChange = (value: string) => {
    setSearchValue(value);
    if (!value) {
      setPage(1);
    }
  };

  const selectedSortLabel =
    sortOptions.find((option) => option.key === sortKey)?.label ??
    sortOptions[0]?.label ??
    '';

  const inventorySortMenu = React.useMemo(
    () => ({
      items: sortOptions.map((option) => ({
        key: option.key,
        label: option.label,
        onClick: () => onSortChange(option.key, sortDirection),
      })),
    }),
    [onSortChange, sortDirection, sortOptions]
  );

  const handleInventorySortOrderToggle = () => {
    onSortChange(sortKey, sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const filteredFilterConfig = React.useMemo(() => {
    if (!isPerDevice) return inventoryFiltersConfig;

    return {
      ...inventoryFiltersConfig,
      groupedFilters: inventoryFiltersConfig.groupedFilters.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => !PER_DEVICE_HIDDEN_FILTER_KEYS.has(item.key)
        ),
      })),
    };
  }, [isPerDevice]);

  const toolbarClassName = [
    'inventory-toolbar',
    isSearchVisible ? 'is-search-open' : '',
    appliedFilters.length > 0 ? 'has-applied-filters' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={toolbarClassName} style={{ marginBottom: 10 }}>
      <Flex
        align='end'
        wrap
        gap={12}
        style={{ marginBottom: 8, marginTop: 24 }}
      >
        <div className='zl-para-400-12 color-5e'>
          Showing {totalRangeStart}-{totalRangeEnd} of {totalCount}
        </div>
      </Flex>

      <Flex align='center' className='inventory-toolbar-row'>
        <Flex
          align='center'
          className='inventory-toolbar-left relative shipment-toolbar-gap'
        >
          <ShipmentSearchControl
            isSearchVisible={isSearchVisible}
            isCompactSearch={isCompactSearch}
            searchPopoverOpen={searchPopoverOpen}
            searchQuery={searchValue}
            appliedSearchQuery={searchValue}
            savedFilterKey='inventory'
            setSearchQuery={handleInventorySearchQueryChange}
            handleSearch={handleInventorySearchSubmit}
            handleCloseSearch={handleInventoryCloseSearch}
            handlePopoverClose={() => setSearchPopoverOpen(false)}
            handleSearchIconClick={handleInventorySearchIconClick}
            setSearchPopoverOpen={setSearchPopoverOpen}
            tableView={true}
          />

          <div className='inventory-groupby-wrap'>
            <InventoryGroupingControl
              groupBy={groupBy}
              subGroupBy={subGroupBy}
              onChange={({
                groupBy: nextGroupBy,
                subGroupBy: nextSubGroupBy,
              }) => {
                setGroupBy((prev) =>
                  prev === nextGroupBy ? prev : nextGroupBy
                );
                setSubGroupBy((prev) =>
                  prev === nextSubGroupBy ? prev : nextSubGroupBy
                );
                setPage(1);
              }}
            />
          </div>

          <div className='inventory-filters-wrap'>
            <ShipmentsFilterPanel
              showSaveFilterSetLink={false}
              addFilterBgColor='var(--light-gray)'
              addFilterFontSize='add-filter-font font-size-11'
              showSearch={false}
              dropdownMaxHeight={dropdownMaxHeight}
              savedFilterKey='inventory'
              filterConfig={filteredFilterConfig}
              token=''
              permissions={null}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              filterArray={filterArray}
              setFilterArray={setFilterArray}
              setFiltersPayload={setFiltersPayload}
              searchQuery={searchValue}
              setSearchQuery={setSearchValue}
              isSearchVisible={false}
              setIsSearchVisible={setIsSearchVisible}
              page={page}
              pageSize={pageSize}
              onRunSearch={onRunSearch}
              registerHandleApplyFilters={() => {}}
              useInternalSaveFilterSetModal={false}
              isShipmentToolbarFilter={true}
            />
          </div>
        </Flex>

        <Flex align='center' gap={8} className='inventory-toolbar-controls'>
          <ShipmentSort
            sortMenu={inventorySortMenu}
            selectedSortLabel={selectedSortLabel}
            sortOrder={sortDirection === 'asc' ? 'ascending' : 'descending'}
            sortKey={sortKey}
            onToggleOrder={handleInventorySortOrderToggle}
          />

          <div className='toolbar-separator' />

          <Segmented
            className='inventory-view-toggle'
            value={inventoryView}
            options={[
              { label: 'Per SKU', value: 'per_sku' },
              { label: 'Per Device', value: 'per_device' },
            ]}
            onChange={(value) =>
              onInventoryViewChange(value as InventoryViewMode)
            }
          />

          <div className='toolbar-separator' />

          <div className='inventory-add-wrap'>
            <AddButton
              addButtonWidth='66px'
              fontSize='12px'
              fontWeight='600'
              text='Add'
              onClick={onAddInventory}
            />
          </div>

          <Tooltip
            title='Download report'
            placement='bottom'
            overlayClassName='inventory-tooltip inventory-download-tooltip'
            overlayInnerStyle={{ color: 'var(--text-primary)', fontSize: 14 }}
            align={{ offset: [-8, 6] }}
          >
            <button
              type='button'
              className='inventory-download-button'
              onClick={onDownload}
              disabled={isDownloading}
              aria-label='Download inventory report'
            >
              <DownloadIcon />
            </button>
          </Tooltip>
        </Flex>
      </Flex>
    </div>
  );
};

export default InventoryToolbar;
